import React, { useState } from 'react'
import { Modal, Radio } from 'antd'
import {
  FormHint,
  FormLabel,
  ModalLayout,
  RadioOptionRow,
  SpacedFormItemWrapper,
} from './ClientBillingProfileManagement.styles'
import type {
  ExportFormat,
  ExportScope,
  ExportScopeOption,
} from './bulkUpload.excel'

interface ExportModalProps<TScope extends ExportScope> {
  open: boolean
  isExporting: boolean
  /** Offered scopes, in order. The first is selected when the modal opens. */
  scopes: ExportScopeOption<TScope>[]
  onClose: () => void
  onDownload: (scope: TScope, format: ExportFormat) => void
}

/** CSV holds one table, so it cannot carry a two-sheet scope. */
const isUnsupportedPair = (scope: ExportScope, format: ExportFormat) =>
  scope === 'BOTH' && format === 'CSV'

/**
 * Shared by both Billing Profile tabs. Each one passes the scopes it can write,
 * and every scope states what lands in the file, so nobody has to guess whether
 * the filters on the page narrowed it.
 */
const ExportModal = <TScope extends ExportScope>({
  open,
  isExporting,
  scopes,
  onClose,
  onDownload,
}: ExportModalProps<TScope>) => {
  const [scope, setScope] = useState<TScope>(scopes[0].value)
  const [format, setFormat] = useState<ExportFormat>('XLSX')

  const active = scopes.find((option) => option.value === scope) ?? scopes[0]
  const blocked = isUnsupportedPair(active.value, format)

  return (
    <Modal
      open={open}
      title="Export"
      onCancel={onClose}
      onOk={() => onDownload(active.value, format)}
      okText="Download"
      okButtonProps={{ disabled: blocked }}
      confirmLoading={isExporting}
      width={480}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      destroyOnClose
    >
      <ModalLayout>
        <div>
          {/* With one scope there is nothing to choose, so the heading would
              label an empty row. The hint still says what the file holds. */}
          {scopes.length > 1 ? (
            <>
              <FormLabel>What to include</FormLabel>
              <Radio.Group
                value={active.value}
                onChange={(event) => setScope(event.target.value)}
              >
                <RadioOptionRow>
                  {scopes.map((option) => (
                    <Radio key={option.value} value={option.value}>
                      {option.label}
                    </Radio>
                  ))}
                </RadioOptionRow>
              </Radio.Group>
            </>
          ) : null}
          <FormHint>{active.hint}</FormHint>
        </div>

        <SpacedFormItemWrapper>
          <FormLabel>Format</FormLabel>
          <Radio.Group
            value={format}
            onChange={(event) => setFormat(event.target.value)}
          >
            <RadioOptionRow>
              <Radio value="XLSX">Excel (.xlsx)</Radio>
              <Radio value="CSV">CSV</Radio>
            </RadioOptionRow>
          </Radio.Group>
          {blocked ? (
            <FormHint>
              CSV holds one table, so pick a single scope or export as Excel.
            </FormHint>
          ) : null}
        </SpacedFormItemWrapper>
      </ModalLayout>
    </Modal>
  )
}

export { ExportModal }
